import { caseApiRequest } from './apiClient';

export type ExtractedCaseOutcomeListRow = {
  extracted_case_outcome_id: string;
  case_request_id: string;
  archive_reference_number: string;
  file_reference_number: string | null;
  file_category_name: string;
  sub_file_category_name: string | null;
  status_id: string;
  status_name: string;
  status_code: string;
  case_completed_at: string | null;
  outcome_extracted_at: string | null;
};

export type ExtractedCaseOutcomeListApiResponse = {
  content: ExtractedCaseOutcomeListRow[];
  page: number;
  size: number;
  total_elements: number;
  total_pages: number;
  message: string | null;
};

export type ExtractedCaseOutcomeListRequestBody = {
  reference_number?: string | null;
  file_reference_number?: string | null;
  file_category_names?: string[] | null;
  sub_file_category_names?: string[] | null;
  status_name?: string | null;
  status_codes?: string[] | null;
  applicable_statutes?: string | null;
  search_text?: string | null;
  case_completed_at_from?: string | null;
  case_completed_at_to?: string | null;
  outcome_extracted_at_from?: string | null;
  outcome_extracted_at_to?: string | null;
  sort_by?: string | null;
  sort_direction?: 'ASC' | 'DESC' | null;
};

export type LegalKnowledgeArchiveListRow = {
  extracted_case_outcome_id: string;
  case_request_id: string;
  archive_reference_number: string;
  file_reference_number: string | null;
  file_category_name: string;
  sub_file_category_name: string | null;
  status_id: string;
  case_completed_at: string | null;
  case_published_at: string | null;
};

export type LegalKnowledgeArchiveListApiResponse = {
  content: LegalKnowledgeArchiveListRow[];
  page: number;
  size: number;
  total_elements: number;
  total_pages: number;
  message: string | null;
};

export type LegalKnowledgeArchiveListRequestBody = {
  reference_number?: string | null;
  file_reference_number?: string | null;
  file_category_ids?: string[] | null;
  sub_file_category_ids?: string[] | null;
  search_text?: string | null;
  case_completed_at_from?: string | null;
  case_completed_at_to?: string | null;
  published_at_from?: string | null;
  published_at_to?: string | null;
  sort_by?: string | null;
  sort_direction?: 'ASC' | 'DESC' | null;
};

export type CaseLinkageRow = {
  linkage_id: string;
  archive_reference_number: string;
  linked_extracted_case_outcome_id?: string;
};

export type ExtractedCaseOutcomeDetail = {
  id: string;
  case_request_id: string;
  archive_reference_number: string;
  file_reference_number: string;
  case_completed_at: string | null;
  outcome_extracted_at: string | null;
  status_code: string;
  status_name: string;
  file_category: string;
  sub_file_category: string | null;
  applicable_areas: string | null;
  request_submitted_at: string | null;
  submitted_agency: string | null;
  division_name: string | null;
  description: string | null;
  province: string | null;
  district: string | null;
  case_number: string | null;
  other_external_reference_numbers: string | null;
  connected_agd_file_reference_number: string | null;
  court_type: string | null;
  court_name: string | null;
  subject_of_the_dispute: string | null;
  incident_occurred_date: string | null;
  applicable_statutes: string | null;
  extracted_outcome_summary: string | null;
  reject_reason: string | null;
  case_linkages: CaseLinkageRow[];
  last_updated_at: string | null;
  last_updated_by: string | null;
};

export type ExtractedCaseOutcomeMutationBody = {
  case_request_id: string;
  applicable_statutes: string;
  extracted_outcome_summary: string;
  link_to_archived_case?: string | null;
};

export type RejectExtractedCaseOutcomeBody = {
  case_request_id: string;
  reject_reason: string;
};

export type CaseLinkageMutationBody = {
  linked_extracted_case_outcome_id: string;
};

export type CaseLinkageMutationResponse = {
  linkage_id: string;
  archive_reference_number: string;
};

export type PublishExtractedCaseOutcomeResponse = {
  status: string;
  response_result: Record<string, unknown>;
  message: string | null;
};

export async function fetchExtractedCaseOutcomeList(
  body: ExtractedCaseOutcomeListRequestBody,
  page: number,
  size: number,
): Promise<ExtractedCaseOutcomeListApiResponse> {
  const res = await caseApiRequest<ExtractedCaseOutcomeListApiResponse>(
    `/v1/legal-knowledge-archive/extracted-case-outcomes/list?page=${page}&size=${size}`,
    { method: 'POST', body },
  );
  if (!res.ok) {
    throw new Error(res.message);
  }
  return res.data;
}

export async function fetchLegalKnowledgeArchiveList(
  body: LegalKnowledgeArchiveListRequestBody,
  page: number,
  size: number,
): Promise<LegalKnowledgeArchiveListApiResponse> {
  const res = await caseApiRequest<LegalKnowledgeArchiveListApiResponse>(
    `/v1/legal-knowledge-archive/list?page=${page}&size=${size}`,
    { method: 'POST', body },
  );
  if (!res.ok) {
    throw new Error(res.message);
  }
  return res.data;
}

export async function fetchExtractedCaseOutcomeById(id: string): Promise<ExtractedCaseOutcomeDetail> {
  const res = await caseApiRequest<ExtractedCaseOutcomeDetail>(
    `/v1/legal-knowledge-archive/extracted-case-outcomes/${id}`,
    { method: 'GET' },
  );
  if (!res.ok) {
    throw new Error(res.message);
  }
  return res.data;
}

export async function fetchExtractedCaseOutcomeByCaseRequest(
  caseRequestId: string,
  status: string,
): Promise<ExtractedCaseOutcomeDetail> {
  const res = await caseApiRequest<ExtractedCaseOutcomeDetail>(
    `/v1/legal-knowledge-archive/extracted-case-outcomes/by-case-request?caseRequestId=${caseRequestId}&status=${status}`,
    { method: 'GET' },
  );
  if (!res.ok) {
    throw new Error(res.message);
  }
  return res.data;
}

export async function resolveArchiveOutcomeIdByReference(
  archiveReferenceNumber: string,
): Promise<string | null> {
  const ref = archiveReferenceNumber.trim();
  if (!ref) return null;

  const matchRef = (archiveRef: string | null | undefined) =>
    archiveRef?.trim().toLowerCase() === ref.toLowerCase();

  const searchBodies: Array<{ reference_number?: string; search_text?: string }> = [
    { reference_number: ref },
    { search_text: ref },
  ];

  for (const body of searchBodies) {
    const published = await fetchLegalKnowledgeArchiveList(body, 0, 100);
    const hit = published.content.find((row) => matchRef(row.archive_reference_number));
    if (hit) return hit.extracted_case_outcome_id;
  }

  for (const body of searchBodies) {
    const outcomes = await fetchExtractedCaseOutcomeList(body, 0, 100);
    const hit = outcomes.content.find((row) => matchRef(row.archive_reference_number));
    if (hit) return hit.extracted_case_outcome_id;
  }

  return null;
}

export function getArchivedCaseDetailsPath(extractedCaseOutcomeId: string): string {
  return `/legal-knowledge/archive/${extractedCaseOutcomeId}`;
}

export async function resolveCaseLinkageTargetIds(
  linkages: CaseLinkageRow[],
): Promise<Record<string, string>> {
  const targets: Record<string, string> = {};
  if (linkages.length === 0) return targets;

  const publishedOptions = await fetchPublishedArchiveOptions();
  const byArchiveRef = new Map(
    publishedOptions.map((option) => [option.label.trim().toLowerCase(), option.id]),
  );

  await Promise.all(
    linkages.map(async (link) => {
      if (link.linked_extracted_case_outcome_id) {
        targets[link.linkage_id] = link.linked_extracted_case_outcome_id;
        return;
      }
      const fromPublished = byArchiveRef.get(link.archive_reference_number.trim().toLowerCase());
      if (fromPublished) {
        targets[link.linkage_id] = fromPublished;
        return;
      }
      const id = await resolveArchiveOutcomeIdByReference(link.archive_reference_number);
      if (id) targets[link.linkage_id] = id;
    }),
  );
  return targets;
}

export function openArchivedCaseDetailsInNewTab(extractedCaseOutcomeId: string): void {
  const url = `${window.location.origin}${getArchivedCaseDetailsPath(extractedCaseOutcomeId)}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

export type LinkableArchivedCase = {
  case_request_id: string;
  extracted_case_outcome_id: string;
  archive_reference_number: string;
};

export async function fetchLinkableArchivedCases(
  caseRequestId: string,
  linkageId?: string,
): Promise<LinkableArchivedCase[]> {
  const params = new URLSearchParams({ caseRequestId });
  if (linkageId) {
    params.set('linkageId', linkageId);
  }
  const res = await caseApiRequest<LinkableArchivedCase[]>(
    `/v1/legal-knowledge-archive/extracted-case-outcomes/linkages/linkable-archived-cases?${params.toString()}`,
    { method: 'GET' },
  );
  if (!res.ok) {
    throw new Error(res.message);
  }
  return res.data;
}

export async function fetchPublishedArchiveOptions(
  excludeId?: string,
): Promise<{ id: string; label: string }[]> {
  const result = await fetchLegalKnowledgeArchiveList({}, 0, 500);
  return result.content
    .filter((row) => row.extracted_case_outcome_id !== excludeId)
    .map((row) => ({
      id: row.extracted_case_outcome_id,
      label: row.archive_reference_number,
    }));
}

export async function retainExtractedCaseOutcomeDraft(
  body: ExtractedCaseOutcomeMutationBody,
): Promise<ExtractedCaseOutcomeDetail> {
  const res = await caseApiRequest<ExtractedCaseOutcomeDetail>(
    '/v1/legal-knowledge-archive/extracted-case-outcomes/retain-draft',
    { method: 'PUT', body },
  );
  if (!res.ok) {
    throw new Error(res.message);
  }
  return res.data;
}

export async function rejectExtractedCaseOutcome(
  body: RejectExtractedCaseOutcomeBody,
): Promise<ExtractedCaseOutcomeDetail> {
  const res = await caseApiRequest<ExtractedCaseOutcomeDetail>(
    '/v1/legal-knowledge-archive/extracted-case-outcomes/reject',
    { method: 'PUT', body },
  );
  if (!res.ok) {
    throw new Error(res.message);
  }
  return res.data;
}

export async function publishExtractedCaseOutcome(
  body: ExtractedCaseOutcomeMutationBody,
): Promise<PublishExtractedCaseOutcomeResponse> {
  const res = await caseApiRequest<PublishExtractedCaseOutcomeResponse>(
    '/v1/legal-knowledge-archive/extracted-case-outcomes/publish',
    { method: 'POST', body },
  );
  if (!res.ok) {
    throw new Error(res.message);
  }
  return res.data;
}

export async function addCaseLinkage(
  extractedCaseOutcomeId: string,
  body: CaseLinkageMutationBody,
): Promise<CaseLinkageMutationResponse> {
  const res = await caseApiRequest<CaseLinkageMutationResponse>(
    `/v1/legal-knowledge-archive/extracted-case-outcomes/${extractedCaseOutcomeId}/linkages`,
    { method: 'POST', body },
  );
  if (!res.ok) {
    throw new Error(res.message);
  }
  return res.data;
}

export async function updateCaseLinkage(
  extractedCaseOutcomeId: string,
  linkageId: string,
  body: CaseLinkageMutationBody,
): Promise<CaseLinkageMutationResponse> {
  const res = await caseApiRequest<CaseLinkageMutationResponse>(
    `/v1/legal-knowledge-archive/extracted-case-outcomes/${extractedCaseOutcomeId}/linkages/${linkageId}`,
    { method: 'PUT', body },
  );
  if (!res.ok) {
    throw new Error(res.message);
  }
  return res.data;
}

export async function deleteCaseLinkage(
  extractedCaseOutcomeId: string,
  linkageId: string,
): Promise<void> {
  const res = await caseApiRequest<unknown>(
    `/v1/legal-knowledge-archive/extracted-case-outcomes/${extractedCaseOutcomeId}/linkages/${linkageId}`,
    { method: 'DELETE' },
  );
  if (!res.ok) {
    throw new Error(res.message);
  }
}

export type LkaDashboardStatisticsResponse = {
  total_published: number;
  draft_count: number;
  rejected_count: number;
};

export type LkaDashboardSubCategoryNode = {
  sub_file_category_name: string;
  count: number;
};

export type LkaDashboardCategoryNode = {
  file_category_name: string;
  total_count: number;
  sub_categories: LkaDashboardSubCategoryNode[];
};

export type LkaDashboardByCategoryResponse = {
  categories: LkaDashboardCategoryNode[];
};

export type LkaDashboardPeriodSubCategoryNode = {
  sub_file_category_name: string;
  count: number;
};

export type LkaDashboardPeriodCategoryNode = {
  file_category_name: string;
  count: number;
  sub_categories: LkaDashboardPeriodSubCategoryNode[];
};

export type LkaDashboardPeriodNode = {
  period_label: string;
  year: number;
  month: number | null;
  count: number;
  categories: LkaDashboardPeriodCategoryNode[];
};

export type LkaDashboardByPeriodResponse = {
  periods: LkaDashboardPeriodNode[];
};

export async function fetchDashboardStatistics(): Promise<LkaDashboardStatisticsResponse> {
  const res = await caseApiRequest<LkaDashboardStatisticsResponse>(
    '/v1/legal-knowledge-archive/dashboard/statistics',
    { method: 'GET' },
  );
  if (!res.ok) throw new Error(res.message);
  return res.data;
}

export async function fetchDashboardByCategory(): Promise<LkaDashboardByCategoryResponse> {
  const res = await caseApiRequest<LkaDashboardByCategoryResponse>(
    '/v1/legal-knowledge-archive/dashboard/by-category',
    { method: 'GET' },
  );
  if (!res.ok) throw new Error(res.message);
  return res.data;
}

export async function fetchDashboardByPeriod(): Promise<LkaDashboardByPeriodResponse> {
  const res = await caseApiRequest<LkaDashboardByPeriodResponse>(
    '/v1/legal-knowledge-archive/dashboard/by-period',
    { method: 'GET' },
  );
  if (!res.ok) throw new Error(res.message);
  return res.data;
}
